import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";

// Payment endpoints now require a token — previously the whole /safepay
// router was open without login, so these calls never sent the header.
const authHeaders = () => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("ots_token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// Create SafePay checkout session
export const CreateSafePaySession = createAsyncThunk(
  "safepay/createSession",
  async ({ paymentData }) => {
    const response = await axios.post(
      `${BASE_URL}/safepay/create-checkout`,
      {
        amount: paymentData.amount,
        billType: paymentData.billType,
        billId: paymentData.billId,
        tenantId: paymentData.tenantId,
        landlordId: paymentData.landlordId,
        flatId: paymentData.flatId,
        description: paymentData.description,
        commissionAmount: paymentData.commissionAmount,
        processingAmount: paymentData.processingAmount,
      },
      authHeaders()
    );
    return response;
  }
);

// Verify SafePay payment
//
// `sig` is the exact name SafePay sends on redirect, and the server reads it
// by that same name — this used to send `signature` instead, which never
// matched.
export const VerifySafePayPayment = createAsyncThunk(
  "safepay/verifyPayment",
  async ({ tracker, signature, sig, reference }) => {
    const response = await axios.post(
      `${BASE_URL}/safepay/verify-payment`,
      { tracker, sig: sig || signature, reference },
      authHeaders()
    );
    return response;
  }
);

// Get payment history
export const GetPaymentHistory = createAsyncThunk(
  "safepay/getHistory",
  async ({ tenantId, flatId }) => {
    const response = await axios.get(
      `${BASE_URL}/safepay/payment-history/${tenantId}/${flatId}`,
      authHeaders()
    );
    return response;
  }
);

// Record manual/offline payment
export const RecordPayment = createAsyncThunk(
  "safepay/recordPayment",
  async ({ paymentData }) => {
    const response = await axios.post(
      `${BASE_URL}/safepay/record-payment`,
      {
        ...paymentData,
        paymentMethod: paymentData.paymentMethod || "safepay",
      },
      authHeaders()
    );
    return response;
  }
);

const SafePayPayment = createSlice({
  name: "safepay",
  initialState: {
    loading: false,
    checkoutUrl: null,
    paymentStatus: null,
    paymentHistory: [],
    error: "",
    currentTransaction: null,
  },
  reducers: {
    resetPayment: (state) => {
      state.loading = false;
      state.checkoutUrl = null;
      state.paymentStatus = null;
      state.error = "";
      state.currentTransaction = null;
    },
    setPaymentStatus: (state, action) => {
      state.paymentStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Create checkout session
    builder.addCase(CreateSafePaySession.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(CreateSafePaySession.fulfilled, (state, action) => {
      state.loading = false;
      state.checkoutUrl = action.payload?.data?.checkoutUrl;
      state.currentTransaction = action.payload?.data?.transaction;
    });
    builder.addCase(CreateSafePaySession.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Verify payment
    builder.addCase(VerifySafePayPayment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(VerifySafePayPayment.fulfilled, (state, action) => {
      state.loading = false;
      state.paymentStatus = action.payload?.data?.status;
    });
    builder.addCase(VerifySafePayPayment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Get payment history
    builder.addCase(GetPaymentHistory.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(GetPaymentHistory.fulfilled, (state, action) => {
      state.loading = false;
      state.paymentHistory = action.payload?.data?.payments || [];
    });
    builder.addCase(GetPaymentHistory.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Record payment
    builder.addCase(RecordPayment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(RecordPayment.fulfilled, (state, action) => {
      state.loading = false;
      state.paymentStatus = "recorded";
    });
    builder.addCase(RecordPayment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
  },
});

export const { resetPayment, setPaymentStatus } = SafePayPayment.actions;
export default SafePayPayment.reducer;
